// components/schedule/ScheduleWeekListView.tsx
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSwipeable } from 'react-swipeable';
import {
    Box, Typography, Paper, Accordion, AccordionSummary, AccordionDetails,
    Chip, Divider, List, ListItem, ListItemText, ListItemIcon,
    IconButton, Tooltip, Button, Collapse, useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ConstructionIcon from '@mui/icons-material/Construction';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PersonIcon from '@mui/icons-material/Person';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import WarningIcon from '@mui/icons-material/Warning';
import SettingsInputComponentIcon from '@mui/icons-material/SettingsInputComponent';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { alpha } from '@mui/material/styles';
import { format, startOfWeek, addDays, isSameDay, parseISO } from 'date-fns';
import { ja } from 'date-fns/locale';
import { CombinedScheduleItem } from '../../types/commonSchedule';
import { useScheduleMasterData } from '../schedule/ScheduleDialog/useScheduleMasterData';
import { AvailableMember, PREFERENCE_TYPES } from '../../types/workPreference';

interface ScheduleWeekListViewProps {
    schedules: CombinedScheduleItem[];  // すべてのスケジュール
    currentDate: Date;
    showBothTeams?: boolean;  // 両方の班を表示するかどうか
    selectedTeam?: 'equipment' | 'scaffolding';  // モバイル表示時に選択中の班
    onDateClick: (date: Date, teamType: 'equipment' | 'scaffolding') => void;
    onScheduleClick: (schedule: CombinedScheduleItem) => void;
    onAddSchedule: (date: Date, teamType: 'equipment' | 'scaffolding', scheduleType?: 'construction' | 'other') => void;
    fetchAvailableMembers: (date: string, departmentType: string) => Promise<AvailableMember[]>;
    onWeekChange: (direction: 'prev' | 'next') => void;
}

const ScheduleWeekListView: React.FC<ScheduleWeekListViewProps> = ({
    schedules,
    currentDate,
    showBothTeams = true,
    selectedTeam = 'equipment',
    onDateClick,
    onScheduleClick,
    onAddSchedule,
    fetchAvailableMembers,
    onWeekChange
}) => {
    const theme = useTheme();

    // 週間の日付を生成
    const weekDays = useMemo(() => {
        const startDay = startOfWeek(currentDate, { weekStartsOn: 1 });
        return Array(7).fill(null).map((_, i) => addDays(startDay, i));
    }, [currentDate]);

    // アコーディオンの開閉状態（チーム別にキー管理）
    const [expandedDay, setExpandedDay] = useState<{ [teamKey: string]: string | null }>({
        equipment: null,
        scaffolding: null
    });

    // 未割当メンバー表示の開閉状態（チーム別にキー管理）
    const [showAvailableMembers, setShowAvailableMembers] = useState<{
        [teamKey: string]: { [dateKey: string]: boolean }
    }>({
        equipment: {},
        scaffolding: {}
    });

    // 未割当メンバー情報を保持する状態（チーム別に管理）
    const [availableMembers, setAvailableMembers] = useState<{
        [dateKey: string]: {
            equipment: AvailableMember[],
            scaffolding: AvailableMember[]
        }
    }>({});

    // 稼働日カレンダー情報
    const { isWorkingDay, fetchWorkingDays } = useScheduleMasterData(true, 'construction');

    // スワイプアニメーション用のstate
    const [swipeAnimation, setSwipeAnimation] = useState<{
        direction: 'prev' | 'next' | null;
        progress: number;
    }>({
        direction: null,
        progress: 0,
    });

    // スワイプハンドラーの設定
    const swipeHandlers = useSwipeable({
        onSwiping: (eventData) => {
            // 水平方向の動きが垂直方向より十分大きい場合のみスワイプとして処理（比率を2.0に増加）
            if (Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY) * 2.0) {
                const direction = eventData.deltaX > 0 ? 'prev' : 'next';
                const progress = Math.min(Math.abs(eventData.deltaX) / 300, 1); // 感度を下げるため分母を200から300に増加
                setSwipeAnimation({ direction, progress });
            }
        },
        onSwiped: (eventData) => {
            // 水平方向の動きが十分大きく（しきい値を50から120に増加）、
            // 垂直方向の動きの2.0倍以上ある場合のみ週を変更
            // さらに、スワイプ速度（velocity）が一定以上ある場合のみ処理する
            if (Math.abs(eventData.deltaX) > 120 &&
                Math.abs(eventData.deltaX) > Math.abs(eventData.deltaY) * 2.0 &&
                Math.abs(eventData.velocity) > 0.3) { // 速度のしきい値を追加
                const direction = eventData.deltaX > 0 ? 'prev' : 'next';
                onWeekChange(direction);
            }
            setSwipeAnimation({ direction: null, progress: 0 });
        },
        trackTouch: true,
        trackMouse: true,  // モバイルのみでスワイプを有効にするため
        delta: 15,          // スワイプ検出のしきい値を10から15に増加
        swipeDuration: 2000,
        preventScrollOnSwipe: false,  // スクロールを許可
        touchEventOptions: { passive: true }  // パフォーマンス向上のためにパッシブに
    });

    // スワイプ視覚フィードバックを改善
    // getAnimationStyle関数を修正
    const getAnimationStyle = useCallback(() => {
        if (!swipeAnimation.direction || swipeAnimation.progress === 0) {
            return {};
        }

        return {
            transform: `translateX(${swipeAnimation.direction === 'prev' ? swipeAnimation.progress * 50 : -swipeAnimation.progress * 50}px)`, // 値を100から50に減少
            opacity: 1 - swipeAnimation.progress * 0.2, // 0.3から0.2に減少（透明度変化を控えめに）
            transition: swipeAnimation.progress === 0 ? 'transform 0.3s ease-out' : 'none',
        };
    }, [swipeAnimation]);


    // 初期アコーディオン状態の設定
    useEffect(() => {
        if (weekDays.length === 0) return;

        // 初期表示時は今日の日付があればそれを開く、なければ最初の日付を開く
        const today = new Date();
        const todayIndex = weekDays.findIndex(day => isSameDay(day, today));
        const todayStr = todayIndex >= 0
            ? format(weekDays[todayIndex], 'yyyy-MM-dd')
            : format(weekDays[0], 'yyyy-MM-dd');

        // 設備班と仮設班の両方に適用
        setExpandedDay({
            equipment: todayStr,
            scaffolding: todayStr
        });
    }, [weekDays]);

    // 稼働日情報を取得
    useEffect(() => {
        if (weekDays.length === 0) return;

        fetchWorkingDays(
            format(weekDays[0], 'yyyy-MM-dd'),
            format(weekDays[6], 'yyyy-MM-dd')
        );
    }, [weekDays, fetchWorkingDays]);

    // 未割当メンバー情報を取得（両方の班について）
    useEffect(() => {
        if (weekDays.length === 0) return;

        const fetchAllMembers = async () => {
            const newAvailableMembers: {
                [dateKey: string]: {
                    equipment: AvailableMember[],
                    scaffolding: AvailableMember[]
                }
            } = {};

            // すべての日付について、設備班と仮設班の両方で未割当メンバーを取得
            for (const day of weekDays) {
                const dateStr = format(day, 'yyyy-MM-dd');

                if (!newAvailableMembers[dateStr]) {
                    newAvailableMembers[dateStr] = {
                        equipment: [],
                        scaffolding: []
                    };
                }

                try {
                    // 設備班の未割当メンバーを取得
                    const equipmentMembers = await fetchAvailableMembers(dateStr, '設備工事');
                    newAvailableMembers[dateStr].equipment = equipmentMembers;

                    // 仮設班の未割当メンバーを取得
                    const scaffoldingMembers = await fetchAvailableMembers(dateStr, '仮設工事');
                    newAvailableMembers[dateStr].scaffolding = scaffoldingMembers;
                } catch (error) {
                    console.error(`Error fetching available members for ${dateStr}:`, error);
                }
            }

            setAvailableMembers(newAvailableMembers);
        };

        fetchAllMembers();
    }, [weekDays, fetchAvailableMembers]);

    // アコーディオンの開閉処理（チーム別）
    const handleAccordionChange = useCallback((teamType: 'equipment' | 'scaffolding', dateStr: string) => (
        _: React.SyntheticEvent,
        isExpanded: boolean
    ) => {
        setExpandedDay(prev => ({
            ...prev,
            [teamType]: isExpanded ? dateStr : null
        }));
    }, []);

    // 未割当メンバー表示の切り替え（チーム別）
    const toggleAvailableMembers = useCallback((teamType: 'equipment' | 'scaffolding', dateStr: string, event?: React.MouseEvent) => {
        if (event) {
            event.stopPropagation();
        }

        setShowAvailableMembers(prev => {
            const teamState = prev[teamType] || {};
            return {
                ...prev,
                [teamType]: {
                    ...teamState,
                    [dateStr]: !teamState[dateStr]
                }
            };
        });
    }, []);

    // 日付ごとの予定を取得（チーム別）
    const getDateSchedules = useCallback((date: Date, teamType: 'equipment' | 'scaffolding') => {
        return schedules.filter(s => {
            const scheduleDate = parseISO(s.scheduleDate);
            return isSameDay(scheduleDate, date) && s.teamType === teamType;
        });
    }, [schedules]);

    // 日付の未割当メンバーを取得（チーム別）
    const getAvailableMembers = useCallback((date: Date, teamType: 'equipment' | 'scaffolding') => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return availableMembers[dateStr]?.[teamType] || [];
    }, [availableMembers]);

    // 勤務希望のアイコンを取得
    const getPreferenceIcon = useCallback((preferenceType: number | null | undefined) => {
        if (preferenceType === PREFERENCE_TYPES.MUST_WORK) {
            return <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: '1rem' }} />;
        } else if (preferenceType === PREFERENCE_TYPES.PREFER_WORK) {
            return <ThumbUpIcon sx={{ color: theme.palette.warning.main, fontSize: '1rem' }} />;
        } else if (preferenceType === PREFERENCE_TYPES.WANT_OFF) {
            return <CloseIcon sx={{ color: theme.palette.error.main, fontSize: '1rem' }} />;
        } else {
            return undefined;
        }
    }, [theme.palette]);

    // 予定追加ボタンクリックハンドラ
    const handleAddScheduleClick = useCallback((day: Date, teamType: 'equipment' | 'scaffolding', scheduleType: 'construction' | 'other', event: React.MouseEvent) => {
        event.stopPropagation();
        onAddSchedule(day, teamType, scheduleType);
    }, [onAddSchedule]);

    // スケジュールクリックハンドラ
    const handleScheduleClick = useCallback((schedule: CombinedScheduleItem, event: React.MouseEvent) => {
        event.stopPropagation();
        onScheduleClick(schedule);
    }, [onScheduleClick]);

    // 会社名フォーマット関数 - 株式会社などの法人格を省略
    const formatCompanyName = useCallback((name: string): string => {
        // 法人格パターンの配列
        const companyPatterns = [
            '株式会社', '有限会社', '合同会社', '合資会社', '社団法人', '財団法人',
            '一般社団法人', '一般財団法人', '公益社団法人', '公益財団法人',
            '学校法人', '医療法人', '宗教法人', '(株)', '㈱'
        ];

        // 法人格が先頭にある場合（例: 株式会社〇〇）
        for (const pattern of companyPatterns) {
            if (name.startsWith(pattern)) {
                return name.substring(pattern.length).trim();
            }
        }

        // 法人格が末尾にある場合（例: 〇〇株式会社）
        for (const pattern of companyPatterns) {
            if (name.endsWith(pattern)) {
                return name.substring(0, name.length - pattern.length).trim();
            }
        }

        // パターンに一致しない場合は元の名前を返す
        return name;
    }, []);

    // 1つの班のリスト表示を生成する関数
    const renderTeamList = useCallback((teamType: 'equipment' | 'scaffolding') => {
        // チーム情報を設定
        const teamTitle = teamType === 'equipment' ? '設備班' : '仮設班';
        const teamIcon = teamType === 'equipment'
            ? <SettingsInputComponentIcon sx={{ mr: 1 }} />
            : <EngineeringIcon sx={{ mr: 1 }} />;
        const teamColor = teamType === 'equipment'
            ? theme.palette.info.main
            : theme.palette.warning.main;

        return (
            <Box sx={{ flex: 1, mb: 2 }}>
                {/* チームタイトル */}
                <Typography
                    variant="subtitle1"
                    sx={{
                        mb: 1,
                        fontWeight: 'medium',
                        display: 'flex',
                        alignItems: 'center',
                        color: teamColor,
                        bgcolor: alpha(teamColor, 0.1),
                        p: 1,
                        borderRadius: 1
                    }}
                >
                    {teamIcon}
                    {teamTitle}
                </Typography>

                {/* 日付ごとのアコーディオン */}
                {weekDays.map(day => {
                    const dateStr = format(day, 'yyyy-MM-dd');
                    const daySchedules = getDateSchedules(day, teamType);
                    const isToday = isSameDay(day, new Date());
                    const isCompanyWorkDay = isWorkingDay(day);
                    const members = getAvailableMembers(day, teamType);

                    return (
                        <Accordion
                            key={`${teamType}-${dateStr}`}
                            expanded={expandedDay[teamType] === dateStr}
                            onChange={handleAccordionChange(teamType, dateStr)}
                            sx={{
                                mb: 1,
                                border: '1px solid',
                                borderColor: isToday ? theme.palette.primary.main : theme.palette.divider,
                                boxShadow: 'none',
                                '&:before': { display: 'none' },
                                bgcolor: isCompanyWorkDay ? 'background.paper' : alpha(theme.palette.grey[200], 0.5),
                            }}
                        >
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                sx={{
                                    borderLeft: '4px solid',
                                    borderLeftColor: teamColor,
                                    py: 1
                                }}
                            >
                                {/* 日付と予定カウント表示 */}
                                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        width: '100%'
                                    }}>
                                        {/* 左側：日付と工事件数をグループ化 */}
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.5
                                        }}>
                                            {/* 日付表示 */}
                                            <Typography
                                                variant="subtitle1"
                                                fontWeight={isToday ? 'bold' : 'medium'}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    color: isToday ? theme.palette.primary.main : 'text.primary'
                                                }}
                                            >
                                                {format(day, 'MM/dd(EEE)', { locale: ja })}
                                                {!isCompanyWorkDay && (
                                                    <Chip
                                                        label="休"
                                                        size="small"
                                                        color="error"
                                                        sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                                                    />
                                                )}
                                            </Typography>

                                            {/* 工事件数表示（日付の右に配置） */}
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                bgcolor: alpha(theme.palette.grey[100], 0.8),
                                                py: 0.2,
                                                px: 1,
                                                borderRadius: 1,
                                                border: `1px solid ${alpha(theme.palette.grey[300], 0.5)}`
                                            }}>
                                                {/* 工事予定カウント */}
                                                <Tooltip title="工事予定">
                                                    <Box sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        mr: 1.5
                                                    }}>
                                                        <Box sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: teamColor,
                                                            mr: 0.5
                                                        }} />
                                                        <Typography variant="caption" fontWeight="medium">
                                                            {daySchedules.filter(s => s.type === 'construction').length}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>

                                                {/* その他予定カウント - 色を変更 */}
                                                <Tooltip title="その他予定">
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        <Box sx={{
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: '50%',
                                                            bgcolor: theme.palette.grey[600],
                                                            mr: 0.5
                                                        }} />
                                                        <Typography variant="caption" fontWeight="medium">
                                                            {daySchedules.filter(s => s.type === 'other').length}
                                                        </Typography>
                                                    </Box>
                                                </Tooltip>
                                            </Box>
                                        </Box>

                                        {/* 右側：予定追加ボタン */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Tooltip title="工事予定追加">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleAddScheduleClick(day, teamType, 'construction', e)}
                                                    sx={{
                                                        p: 0.5,
                                                        color: teamColor
                                                    }}
                                                >
                                                    <Box sx={{ position: 'relative' }}>
                                                        <ConstructionIcon fontSize="small" />
                                                        <AddIcon
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: -4,
                                                                right: -4,
                                                                fontSize: '0.85rem',
                                                                bgcolor: 'background.paper',
                                                                borderRadius: '50%',
                                                                p: 0.1
                                                            }}
                                                        />
                                                    </Box>
                                                </IconButton>
                                            </Tooltip>

                                            <Tooltip title="その他予定追加">
                                                <IconButton
                                                    size="small"
                                                    onClick={(e) => handleAddScheduleClick(day, teamType, 'other', e)}
                                                    sx={{
                                                        p: 0.5,
                                                        color: theme.palette.grey[700]
                                                    }}
                                                >
                                                    <Box sx={{ position: 'relative' }}>
                                                        <MeetingRoomIcon fontSize="small" />
                                                        <AddIcon
                                                            sx={{
                                                                position: 'absolute',
                                                                bottom: -4,
                                                                right: -4,
                                                                fontSize: '0.85rem',
                                                                bgcolor: 'background.paper',
                                                                borderRadius: '50%',
                                                                p: 0.1
                                                            }}
                                                        />
                                                    </Box>
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                            </AccordionSummary>

                            <AccordionDetails sx={{ p: 0 }}>
                                {/* 予定がない場合のメッセージ */}
                                {daySchedules.length === 0 ? (
                                    <Box
                                        sx={{
                                            p: 2,
                                            textAlign: 'center',
                                            color: 'text.secondary'
                                        }}
                                    >
                                        <Typography variant="body2">予定はありません</Typography>
                                    </Box>
                                ) : (
                                    <List disablePadding>
                                        {daySchedules.map(schedule => (
                                            <ListItem
                                                key={schedule.id}
                                                button
                                                divider
                                                onClick={(e) => handleScheduleClick(schedule, e)}
                                                sx={{
                                                    borderLeft: '3px solid',
                                                    borderLeftColor: schedule.type === 'other'
                                                        ? theme.palette.warning.main
                                                        : teamColor,
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.action.hover, 0.1),
                                                    }
                                                }}
                                            >
                                                {/* 予定アイコン */}
                                                <Box sx={{
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    position: 'relative',  // 位置指定のために追加
                                                    minWidth: 40
                                                }}>
                                                    {/* 予定アイコン */}
                                                    {/* {schedule.type === 'other' ? (
                                                        <MeetingRoomIcon color="warning" />
                                                    ) : (
                                                        <ConstructionIcon color={teamType === 'equipment' ? 'info' : 'warning'} />
                                                    )} */}

                                                    {/* 集合時間バッジ - アイコンの左上に小さく配置 */}
                                                    {schedule.meetingTime && schedule.meetingTime !== schedule.startTime && (
                                                        <Chip
                                                            label={`集${schedule.meetingTime.substring(0, 5)}`}
                                                            size="small"
                                                            sx={{
                                                                position: 'absolute',
                                                                top: -10,
                                                                left: -15,
                                                                height: 20,              // 高さを増加
                                                                minWidth: '50px',        // 最小幅を設定
                                                                fontSize: '0.7rem',     // フォントサイズを大きく
                                                                bgcolor: alpha(theme.palette.secondary.main, 0.9),
                                                                color: 'white',
                                                                '& .MuiChip-label': {
                                                                    px: 0.5,
                                                                    py: 0,
                                                                    fontWeight: 'bold'
                                                                },
                                                                zIndex: 1,
                                                                boxShadow: '0 1px 3px rgba(0,0,0,0.2)'
                                                            }}
                                                        />
                                                    )}
                                                </Box>

                                                {/* 予定内容 */}
                                                <ListItemText
                                                    primary={
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            {/* タイトル（現場名/内容） */}
                                                            <Typography variant="subtitle2" component="span" noWrap sx={{ flex: 1 }}>
                                                                {schedule.type === 'other'
                                                                    ? (schedule.scheduleContent || 'その他予定')
                                                                    : (schedule.siteName || `現場 ${schedule.constructionId}`)
                                                                }
                                                            </Typography>

                                                            {/* 客先名 (同じ行に配置) */}
                                                            {schedule.customerName && (
                                                                <Typography variant="caption" component="span" noWrap sx={{ color: 'text.secondary' }}>
                                                                    {formatCompanyName(schedule.customerName)}
                                                                </Typography>
                                                            )}

                                                            {/* 時間表示 (右寄せ) */}
                                                            {!schedule.isAllDay && schedule.startTime && (
                                                                <Typography variant="caption" sx={{ ml: 'auto', whiteSpace: 'nowrap' }}>
                                                                    {schedule.startTime.substring(0, 5)}
                                                                    {schedule.endTime && ` 〜 ${schedule.endTime.substring(0, 5)}`}
                                                                </Typography>
                                                            )}

                                                            {schedule.isAllDay && (
                                                                <Chip
                                                                    label="終日"
                                                                    size="small"
                                                                    variant="outlined"
                                                                    sx={{ ml: 'auto', height: 18, fontSize: '0.65rem' }}
                                                                />
                                                            )}
                                                        </Box>
                                                    }
                                                    // Typography要素の代わりにスタイル化したspanを使用
                                                    secondaryTypographyProps={{ component: 'span' }}
                                                    secondary={
                                                        <>
                                                            {/* メンバー名表示 */}
                                                            {schedule.memberData && schedule.memberData.length > 0 ? (
                                                                <span style={{ display: 'block', marginTop: '4px' }}>
                                                                    {schedule.memberData.map((member, index) => (
                                                                        <Chip
                                                                            key={`member-${schedule.id}-${index}`}
                                                                            label={member.displayName || member.LastName || member.VendorMemberName || "名前なし"}
                                                                            size="small"
                                                                            sx={{
                                                                                height: 20,
                                                                                fontSize: '0.65rem',
                                                                                bgcolor: member.VendorMember_ID ? alpha(theme.palette.warning.light, 0.2) : alpha(theme.palette.primary.light, 0.1),
                                                                                '& .MuiChip-label': {
                                                                                    px: 0.75,
                                                                                    py: 0
                                                                                },
                                                                                mr: 0.5,
                                                                                mb: 0.5
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </span>
                                                            ) : (
                                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                                    メンバーなし
                                                                </Typography>
                                                            )}

                                                            {/* 車両情報表示 - メンバー名の下に追加 */}
                                                            {schedule.vehicleData && schedule.vehicleData.length > 0 && (
                                                                <span style={{ display: 'block', marginTop: '4px' }}>
                                                                    {schedule.vehicleData.map((vehicle, index) => (
                                                                        <Chip
                                                                            key={`vehicle-${schedule.id}-${index}`}
                                                                            label={vehicle.Car_Nickname || vehicle.Car_Model || vehicle.Car_Number || `車両 ${vehicle.Car_ID}`}
                                                                            size="small"
                                                                            sx={{
                                                                                height: 20,
                                                                                fontSize: '0.65rem',
                                                                                bgcolor: alpha(theme.palette.info.light, 0.1), // メンバーとは異なる背景色
                                                                                border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`, // 境界線を追加して区別
                                                                                '& .MuiChip-label': {
                                                                                    px: 0.75,
                                                                                    py: 0
                                                                                },
                                                                                mr: 0.5,
                                                                                mb: 0.5
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </span>
                                                            )}
                                                        </>
                                                    }
                                                />


                                                {/* メンバー数バッジ - リスト項目の右端に配置 */}
                                                {/* {((schedule.memberData && schedule.memberData.length > 0) || schedule.requiredWorkers) && ( */}
                                                {!((schedule.requiredWorkers === 0 || schedule.requiredWorkers === undefined) && (schedule.memberData?.length || 0) === 0) && (
                                                    <Box sx={{ ml: 1 }}>
                                                        <Chip
                                                            icon={<PersonIcon fontSize="small" />}
                                                            label={`${schedule.memberData?.length || 0}${schedule.requiredWorkers ? `/${schedule.requiredWorkers}` : ''}`}
                                                            size="small"
                                                            color={
                                                                schedule.requiredWorkers &&
                                                                    (schedule.memberData?.length || 0) < schedule.requiredWorkers
                                                                    ? 'error'
                                                                    : 'success'
                                                            }
                                                            sx={{
                                                                height: 24,
                                                                minWidth: '48px',
                                                                '& .MuiSvgIcon-root': {
                                                                    fontSize: '0.85rem'
                                                                }
                                                            }}
                                                        />
                                                    </Box>
                                                )}
                                            </ListItem>
                                        ))}
                                    </List>
                                )}

                                {/* 未割当メンバー情報 */}
                                {members.length > 0 && (
                                    <Box sx={{ p: 1 }}>
                                        <Divider sx={{ my: 1 }}>
                                            <Chip
                                                icon={<PersonOffIcon sx={{ fontSize: '0.8rem' }} />}
                                                label="未割当メンバー"
                                                size="small"
                                                onClick={(e) => toggleAvailableMembers(teamType, dateStr, e)}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    height: 24,
                                                    '& .MuiChip-label': { px: 1 }
                                                }}
                                            />
                                        </Divider>

                                        <Collapse in={showAvailableMembers[teamType]?.[dateStr] || false}>
                                            {/* 未割当メンバーリスト */}
                                            <Box sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 0.5,
                                                p: 1
                                            }}>
                                                {/* 出勤希望あり */}
                                                {members
                                                    .filter(m => m.Preference_Type === PREFERENCE_TYPES.MUST_WORK || m.Preference_Type === PREFERENCE_TYPES.PREFER_WORK)
                                                    .map(member => (
                                                        <Tooltip
                                                            key={`avail-${teamType}-${member.ID}`}
                                                            title={
                                                                member.Preference_Type === PREFERENCE_TYPES.MUST_WORK
                                                                    ? '出勤したい'
                                                                    : 'できれば出勤したい'
                                                            }
                                                            arrow
                                                        >
                                                            <Chip
                                                                icon={getPreferenceIcon(member.Preference_Type)}
                                                                label={member.LastName}
                                                                size="small"
                                                                sx={{
                                                                    height: 24,
                                                                    bgcolor: member.Preference_Type === PREFERENCE_TYPES.MUST_WORK
                                                                        ? alpha(theme.palette.success.light, 0.3)
                                                                        : alpha(theme.palette.warning.light, 0.3)
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ))
                                                }

                                                {/* 休み希望 */}
                                                {members
                                                    .filter(m => m.Preference_Type === PREFERENCE_TYPES.WANT_OFF)
                                                    .map(member => (
                                                        <Tooltip
                                                            key={`avail-${teamType}-${member.ID}`}
                                                            title="休みたい"
                                                            arrow
                                                        >
                                                            <Chip
                                                                icon={getPreferenceIcon(member.Preference_Type)}
                                                                label={member.LastName}
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{
                                                                    height: 24,
                                                                    borderColor: theme.palette.error.main,
                                                                    color: theme.palette.error.main
                                                                }}
                                                            />
                                                        </Tooltip>
                                                    ))
                                                }

                                                {/* 希望未設定 */}
                                                {members
                                                    .filter(m => m.Preference_Type === null || m.Preference_Type === undefined)
                                                    .map(member => (
                                                        <Chip
                                                            key={`avail-${teamType}-${member.ID}`}
                                                            label={member.LastName}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                height: 24,
                                                                borderColor: theme.palette.grey[400],
                                                                color: theme.palette.text.secondary
                                                            }}
                                                        />
                                                    ))
                                                }
                                            </Box>
                                        </Collapse>

                                        {/* 未割当メンバー表示切り替えボタン */}
                                        {!showAvailableMembers[teamType]?.[dateStr] && (
                                            <Button
                                                variant="text"
                                                size="small"
                                                onClick={(e) => toggleAvailableMembers(teamType, dateStr, e)}
                                                sx={{ width: '100%', mt: 0.5 }}
                                            >
                                                未割当メンバー {members.length}人 表示
                                            </Button>
                                        )}
                                    </Box>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    );
                })}
            </Box>
        );
    }, [
        theme,
        weekDays,
        expandedDay,
        showAvailableMembers,
        getDateSchedules,
        getAvailableMembers,
        isWorkingDay,
        handleAccordionChange,
        toggleAvailableMembers,
        handleAddScheduleClick,
        handleScheduleClick,
        getPreferenceIcon,
        formatCompanyName
    ]);

    // モバイル表示時はselectedTeamのみ表示、それ以外は両方表示
    return (
        <div
            {...swipeHandlers}
            style={{
                width: '100%',
                // touchAction: 'none' を削除または以下のように変更
                touchAction: 'pan-y',  // 垂直方向のスクロールを許可
                WebkitTouchCallout: 'none',
                WebkitUserSelect: 'none',
                KhtmlUserSelect: 'none',
                MozUserSelect: 'none',
                msUserSelect: 'none',
                userSelect: 'none'
            }}
        >
            <Box sx={{
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 2,
                ...getAnimationStyle()
            }}>
                {/* 設備班（showBothTeamsがfalseの場合は、selectedTeamが'equipment'の場合のみ表示） */}
                {(showBothTeams || selectedTeam === 'equipment') && renderTeamList('equipment')}

                {/* 仮設班（showBothTeamsがfalseの場合は、selectedTeamが'scaffolding'の場合のみ表示） */}
                {(showBothTeams || selectedTeam === 'scaffolding') && renderTeamList('scaffolding')}
            </Box>
        </div>
    );
};

export default ScheduleWeekListView;